import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import api from "../../../api/axios";

/* AdminUser.role is a number; only role 1 is unbound from a single store and
   allowed to reshape the estate. The store endpoints enforce this — the page
   mirrors it so a scoped admin is not offered buttons that always 403. */
const SUPERADMIN_ROLE = 1;

const LABEL = {
  display: "block", fontSize: 11, fontWeight: 700, letterSpacing: 1,
  color: "var(--mly-text-muted)", marginBottom: 8, textTransform: "uppercase",
};

const INPUT = {
  width: "100%", padding: "11px 14px", borderRadius: 10,
  border: "1.5px solid var(--mly-border)", fontSize: 14, boxSizing: "border-box", outline: "none",
};

const SECTION_TITLE = {
  fontSize: 15, fontWeight: 700, color: "var(--mly-text)",
  paddingBottom: 10, marginBottom: 20, borderBottom: "1px solid var(--mly-border-soft)",
  display: "flex", alignItems: "center", gap: 8,
};

const focusOn  = (e) => { e.target.style.borderColor = "var(--mly-heading)"; };
const focusOff = (e) => { e.target.style.borderColor = "var(--mly-border)"; };

const num = (v, fallback) => {
  const n = parseFloat(v);
  return isNaN(n) ? fallback : n;
};

/* the settings endpoints speak paise; every ₹ box on this form is rupees */
const paiseToRupees = (p) => {
  const n = Number(p);
  return Number.isFinite(n) ? String(n / 100) : "0";
};

/* ── Fields core.MstStores has no column for ───────────────────────────
   The store record is id / code / name / is_default / is_active and
   nothing else. phone, latitude, longitude, deliveryArea,
   deliveryRadiusKm, timezone, hasMill and millingSourceStore are refused
   BY NAME (400 not_portable) and the refusal aborts the whole save, so
   they are shown disabled rather than collected and silently dropped.

   TODO(cutover): no server endpoint for the store map pin, the drawn
   delivery area / radius, the phone number, the timezone or the milling
   routing (hasMill + millingSourceStore). The Leaflet pin-and-polygon
   picker that used to sit in this modal was removed with them: nothing
   it produced could be stored, and a map that discards every click is
   worse than one that is not offered. */
const NOT_STORED_CHIP = {
  fontSize: 9.5, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase",
  padding: "2px 8px", borderRadius: 999, whiteSpace: "nowrap",
  background: "var(--mly-surface-3)", color: "var(--mly-text-muted)",
  border: "1px solid var(--mly-border)",
};

const NotStored = ({ why, children }) => (
  <div>
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      marginBottom: 6, flexWrap: "wrap", minHeight: 20,
    }}>
      <span style={NOT_STORED_CHIP}>Not stored yet</span>
      <span style={{ fontSize: 11, color: "var(--mly-text-muted)" }}>{why}</span>
    </div>
    {/* a disabled fieldset greys the control AND takes it out of the tab
        order, which a pointer-events trick would not */}
    <fieldset
      disabled
      style={{ display: "block", border: 0, padding: 0, margin: 0, minWidth: 0, opacity: 0.55 }}
    >
      {children}
    </fieldset>
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════ */
const Stores = () => {
  /* A scoped admin still lands here — GET /admin/stores hands it back its own
     store and PATCH /admin/stores/{id}/toggle lets it pause that store — but
     every other write is superadmin-only, so those controls are not rendered
     for it. */
  const role       = useSelector((state) => state.adminUser.role);
  const canManage  = Number(role) === SUPERADMIN_ROLE;

  const [stores,  setStores]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editItem,  setEditItem]  = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [formError, setFormError] = useState("");

  const [name,        setName]        = useState("");
  const [code,        setCode]        = useState("");
  const [addressLine, setAddressLine] = useState("");
  const [phone,       setPhone]       = useState("");

  const [deliveryEnabled,   setDeliveryEnabled]   = useState(true);
  const [pickupEnabled,     setPickupEnabled]     = useState(true);
  const [deliveryCharge,    setDeliveryCharge]    = useState("0");
  const [freeDeliveryAbove, setFreeDeliveryAbove] = useState("0");
  const [minOrderValue,     setMinOrderValue]     = useState("0");

  const [timezone,  setTimezone]  = useState("Asia/Kolkata");
  const [openTime,  setOpenTime]  = useState("");
  const [closeTime, setCloseTime] = useState("");

  const [hasMill,            setHasMill]            = useState(false);
  const [millingSourceStore, setMillingSourceStore] = useState("");

  /* Hours, pickup/delivery and the three ₹ boxes are no longer on the store
     row: they live on shop.CfgStoreSettings and are read back through
     /admin/store-settings/resolved (an existing store) or /admin/store-settings
     (a new one, which starts on the estate defaults). Until that read lands the
     form is showing component defaults, not real values — posting them would
     overwrite the stored ones, so the save leaves them out entirely. */
  const [settingsReady,   setSettingsReady]   = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  /* What the read put in the boxes. ONLY the fields the operator has since
     changed are sent.

     A settings key in the body makes the server give this store a settings row
     of its own, seeded by COPYING the estate defaults — which freezes them, so
     later estate-wide edits stop reaching this store. Posting the whole block
     every time would do that on a rename, and would have written the form's
     blank "0"s over a new store's inherited delivery fee, free-delivery
     threshold and minimum order. Sending nothing when nothing changed leaves
     the store genuinely inheriting, which is what the schema means by it. */
  const settingsBaseline = useRef(null);
  /* opening one store's modal straight after another leaves two resolves in
     flight; only the newest may write into the form */
  const editSeq = useRef(0);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      /* the new list is a BARE JSON array — no { status, message, data } */
      const res  = await api.get("/admin/stores");
      setStores(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e.response?.data?.message || "Failed to load stores");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  /* TODO(cutover): server does not return hasMill, so this list is always
     empty and the "Milled By" picker only ever offers "— none —". */
  const millStores = useMemo(
    () => stores.filter((s) => s.hasMill && s.id !== editItem?.id),
    [stores, editItem]
  );

  const resetForm = () => {
    setName(""); setCode(""); setAddressLine(""); setPhone("");
    setDeliveryEnabled(true); setPickupEnabled(true);
    setDeliveryCharge("0"); setFreeDeliveryAbove("0"); setMinOrderValue("0");
    setTimezone("Asia/Kolkata"); setOpenTime(""); setCloseTime("");
    setHasMill(false); setMillingSourceStore("");
    setSettingsReady(false); setSettingsLoading(false);
    settingsBaseline.current = null;
    setFormError("");
  };

  /* Everything the settings half of the form holds, in form shape — written
     into the boxes and kept as the baseline the save diffs against. */
  const applySettings = (s, address) => {
    const shown = {
      addressLine:       address,
      openTime:          s.openTime || "",
      closeTime:         s.closeTime || "",
      pickupEnabled:     s.pickupEnabled !== false,
      deliveryEnabled:   s.deliveryEnabled !== false,
      deliveryCharge:    paiseToRupees(s.deliveryFeePaise),
      freeDeliveryAbove: paiseToRupees(s.freeDeliveryAbovePaise),
      minOrderValue:     paiseToRupees(s.minOrderPaise),
    };
    setAddressLine(shown.addressLine);
    setOpenTime(shown.openTime);
    setCloseTime(shown.closeTime);
    setPickupEnabled(shown.pickupEnabled);
    setDeliveryEnabled(shown.deliveryEnabled);
    setDeliveryCharge(shown.deliveryCharge);
    setFreeDeliveryAbove(shown.freeDeliveryAbove);
    setMinOrderValue(shown.minOrderValue);
    settingsBaseline.current = shown;
    setSettingsReady(true);
  };

  /* A new store starts on the estate-wide defaults — the row every store
     inherits until it overrides something — so the form shows what this store
     will actually operate on rather than a hardcoded ₹0 that used to be posted
     as if it were a decision. */
  const openAdd = async () => {
    const seq = editSeq.current + 1;
    editSeq.current = seq;

    setEditItem(null);
    resetForm();
    setShowModal(true);

    setSettingsLoading(true);
    try {
      const res = await api.get("/admin/store-settings");
      if (seq !== editSeq.current) return;
      applySettings(res.data || {}, "");
    } catch (e) {
      if (seq !== editSeq.current) return;
      setFormError(
        `${e.response?.data?.message || "Failed to read the estate-wide defaults"}. ` +
        "Hours, pickup/delivery and the ₹ fields below are NOT the values this store will " +
        "start on and will not be saved — it will inherit the estate defaults."
      );
    } finally {
      if (seq === editSeq.current) setSettingsLoading(false);
    }
  };

  const openEdit = async (item) => {
    const seq = editSeq.current + 1;
    editSeq.current = seq;

    setEditItem(item);
    setName(item.name || "");
    setCode(item.code || "");
    setAddressLine(item.addressLine || "");
    /* TODO(cutover): server does not return phone, timezone, hasMill or
       millingSourceStore — core.MstStores has no columns for them. */
    setPhone("");
    setTimezone("Asia/Kolkata");
    setHasMill(false);
    setMillingSourceStore("");
    setFormError("");
    setShowModal(true);

    /* The resolved read is the estate defaults with this store's own row on
       top, which is exactly what the store is operating on today. */
    setSettingsReady(false);
    setSettingsLoading(true);
    try {
      const res = await api.get(
        `/admin/store-settings/resolved?storeId=${encodeURIComponent(item.id)}`
      );
      if (seq !== editSeq.current) return;
      const s = res.data || {};
      /* the list row carries only the branch's OWN address (blank when it has
         no row of its own); the resolved read fills in what it inherits */
      applySettings(s, item.addressLine || s.addressLine || "");
    } catch (e) {
      if (seq !== editSeq.current) return;
      setFormError(
        `${e.response?.data?.message || "Failed to read this store's settings"}. ` +
        "Hours, pickup/delivery and the ₹ fields below are NOT this store's values and will not be saved."
      );
    } finally {
      if (seq === editSeq.current) setSettingsLoading(false);
    }
  };

  const close = () => {
    editSeq.current += 1;
    setShowModal(false);
    setEditItem(null);
    resetForm();
  };

  const save = async () => {
    if (!name.trim())  { setFormError("Store name is required"); return; }
    if (!code.trim())  { setFormError("Store code is required — it labels the store on orders"); return; }

    setSaving(true);
    setFormError("");

    /* ONLY what core.MstStores and shop.CfgStoreSettings can hold. The body is
       validated key by key and the FIRST unportable one aborts the whole save
       before anything is written, so a stray phone or map pin here does not
       fail one field — it fails the store. */
    const payload = {
      name: name.trim(),
      code: code.trim().toUpperCase(),
    };
    /* Only what the operator actually changed. A settings key present in the
       body gives this store a settings row of its own, copied from the estate
       and frozen there — so an untouched settings block must not travel. */
    const was = settingsBaseline.current;
    if (settingsReady && was) {
      if (addressLine.trim() !== was.addressLine.trim()) payload.addressLine = addressLine.trim();
      if (pickupEnabled   !== was.pickupEnabled)   payload.pickupEnabled   = pickupEnabled;
      if (deliveryEnabled !== was.deliveryEnabled) payload.deliveryEnabled = deliveryEnabled;
      /* rupees: the server multiplies by 100 and rounds. Sending both
         spellings of one setting is a 400, so only these travel. Compared as
         numbers — "40" and "40.0" are the same fee, not an edit. */
      if (num(deliveryCharge, 0) !== num(was.deliveryCharge, 0))
        payload.deliveryCharge = num(deliveryCharge, 0);
      if (num(freeDeliveryAbove, 0) !== num(was.freeDeliveryAbove, 0))
        payload.freeDeliveryAbove = num(freeDeliveryAbove, 0);
      if (num(minOrderValue, 0) !== num(was.minOrderValue, 0))
        payload.minOrderValue = num(minOrderValue, 0);
      /* open_at / close_at are NOT NULL and a blank is a 400 — an emptied box
         means "leave the stored hours alone", not "clear them" */
      if (openTime  && openTime  !== was.openTime)  payload.openTime  = openTime;
      if (closeTime && closeTime !== was.closeTime) payload.closeTime = closeTime;
    }

    try {
      const res = editItem
        ? await api.put(`/admin/stores/${editItem.id}`, payload)
        : await api.post(`/admin/stores`, payload);
      /* the reply is the payload itself — { id, code, name, isDefault,
         isActive, settingsRowSeededFromEstate } — not the full store row, so
         the list is still re-read */
      const seeded = res.data?.settingsRowSeededFromEstate === true;
      await load();
      close();
      if (seeded) {
        window.alert(
          "This store had no settings of its own, so one row was created by copying the " +
          "estate-wide defaults. Its values match the estate today, but later changes to " +
          "the estate defaults will no longer reach this store."
        );
      }
    } catch (e) {
      setFormError(e.response?.data?.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (id) => {
    try {
      await api.patch(`/admin/stores/${id}/toggle`);
      load();
    } catch (e) {
      /* the default store refuses to be switched off (409
         default_must_stay_enabled) and a scoped admin may only touch its own
         (403) — both arrive as real HTTP failures now, with the reason in
         the body's message */
      alert(e.response?.data?.message || "Failed to toggle");
    }
  };

  /* TODO(cutover): DELETE /admin/stores/{id} exists but can never succeed —
     it counts what still points at the store and always answers 409
     delete_refused, because the service holds no DELETE grant on
     core.MstStores. The button is kept so the refusal (a full sentence
     naming the blockers) reaches the operator instead of silence; pausing
     the store with the Active switch is the real outcome. */
  const remove = async (item) => {
    if (!window.confirm(
      `Delete "${item.name}"? A store with orders, stock or staff behind it cannot be ` +
      "deleted — pause it with the Active switch instead."
    )) return;
    try {
      await api.delete(`/admin/stores/${item.id}`);
      load();
    } catch (e) {
      alert(e.response?.data?.message || "Failed to delete");
    }
  };

  const pill = (text, bg, color, border) => (
    <span style={{
      display: "inline-block", background: bg, color,
      padding: "3px 10px", borderRadius: 12, fontSize: 11,
      fontWeight: 700, whiteSpace: "nowrap",
      border: border ? `1px solid ${border}` : "none",
    }}>
      {text}
    </span>
  );

  return (
    <div>
      <div className="container-fluid">
        <div className="card" style={{ minHeight: "calc(100vh - 72px)" }}>
          <div className="card-header d-flex justify-content-between align-items-center">
            <div>
              <h5 style={{ margin: 0, fontWeight: 700 }}>Stores</h5>
              <span style={{ fontSize: 12, color: "var(--mly-text-muted)" }}>
                {canManage
                  ? "Every store shares the same catalogue and prices. Stock, staff and the store's own hours and delivery rules are what differ."
                  : "This is the store your account is bound to. You can pause or resume it; only a superadmin can change its details."}
              </span>
            </div>
            {canManage && (
              <button className="btn btn-primary btn-sm widthbtn" onClick={openAdd}>
                + Add
              </button>
            )}
          </div>

          <div className="card-body">
            {loading ? (
              <div className="text-center py-4">Loading...</div>
            ) : error ? (
              <div className="text-danger text-center py-4">{error}</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                {/* TODO(cutover): server does not return deliveryArea,
                    deliveryRadiusKm, deliveryEnabled, hasMill or
                    millingSourceStore on a store row, so the old "Delivery
                    Area" and "Mill" columns had nothing left to show — they
                    would have read "Radius · 0 km" and "Not set" for every
                    store — and were dropped rather than faked. */}
                <table className="display table table-bordered">
                  <thead>
                    <tr>
                      <th style={{ minWidth: 60 }}>#</th>
                      <th style={{ minWidth: 200 }}>Store</th>
                      <th style={{ minWidth: 100 }}>Code</th>
                      <th style={{ minWidth: 90 }}>Active</th>
                      {canManage && <th style={{ minWidth: 110 }}>Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {stores.length === 0 ? (
                      <tr>
                        <td colSpan={canManage ? 5 : 4} style={{ textAlign: "center", padding: "40px 0", color: "var(--mly-text-muted)" }}>
                          {canManage
                            ? "No stores yet — add one to start splitting stock by branch."
                            : "No store is bound to your account yet — ask a superadmin to assign you one."}
                        </td>
                      </tr>
                    ) : (
                      stores.map((item, index) => (
                        <tr key={item.id}>
                          <td>{index + 1}</td>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                              <span style={{ fontWeight: 600 }}>{item.name}</span>
                              {item.isDefault &&
                                pill("DEFAULT", "#FBF3DF", "#8F5E18", "#EFD18C")}
                            </div>
                            {item.addressLine && (
                              <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginTop: 2 }}>
                                {item.addressLine}
                              </div>
                            )}
                          </td>
                          <td>
                            <span style={{ fontFamily: "monospace", fontSize: 13 }}>
                              {item.code || "-"}
                            </span>
                          </td>
                          <td>
                            <label className="switch">
                              <input
                                type="checkbox"
                                checked={!!item.isActive}
                                onChange={() => toggle(item.id)}
                              />
                              <span className="slider round"></span>
                            </label>
                          </td>
                          {canManage && (
                            <td className="d-flex gap-2">
                              <button
                                onClick={() => openEdit(item)}
                                title="Edit"
                                style={{
                                  width: 36, height: 36, borderRadius: 8, border: "none",
                                  background: "var(--mly-accent-soft)", color: "var(--mly-heading)",
                                  cursor: "pointer",
                                }}
                              >
                                ✎
                              </button>
                              <button
                                onClick={() => remove(item)}
                                disabled={item.isDefault}
                                title={item.isDefault
                                  ? "The default store is what every legacy row points at — it cannot be deleted"
                                  : "Delete"}
                                style={{
                                  width: 36, height: 36, borderRadius: 8, border: "none",
                                  background: item.isDefault ? "var(--mly-surface-3)" : "#fff5f5",
                                  color: item.isDefault ? "var(--mly-text-muted)" : "#ef4444",
                                  cursor: item.isDefault ? "not-allowed" : "pointer",
                                }}
                              >
                                🗑
                              </button>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <>
          <div
            onClick={close}
            style={{
              position: "fixed", inset: 0,
              background: "rgba(0,0,0,0.45)",
              backdropFilter: "blur(2px)", zIndex: 1040,
            }}
          />
          <div
            style={{
              position: "fixed", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1050, width: "100%", maxWidth: 720,
              background: "var(--mly-surface)", borderRadius: 20,
              boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
              padding: "28px 32px",
              maxHeight: "92vh", overflowY: "auto",
            }}
          >
            <div style={{
              display: "flex", alignItems: "center",
              justifyContent: "space-between", marginBottom: 20,
            }}>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>
                {editItem ? "Edit Store" : "New Store"}
              </h2>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {editItem?.isDefault &&
                  pill("DEFAULT STORE", "#FBF3DF", "#8F5E18", "#EFD18C")}
                <button
                  onClick={close}
                  style={{
                    width: 32, height: 32, borderRadius: "50%",
                    border: "1px solid var(--mly-border)", background: "var(--mly-surface-2)",
                    cursor: "pointer", fontSize: 16,
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            {formError && (
              <div style={{
                background: "#fef2f2", color: "#dc2626",
                padding: "10px 14px", borderRadius: 8,
                marginBottom: 16, fontSize: 13,
              }}>
                {formError}
              </div>
            )}

            {/* ── Store Details ── */}
            <div style={SECTION_TITLE}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--mly-heading)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
              Store Details
            </div>

            <div className="row" style={{ marginBottom: 16 }}>
              <div className="col-md-8">
                <label style={LABEL}>Store Name <span style={{ color: "#ef4444" }}>*</span></label>
                <input
                  type="text" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. MilliyO Peelamedu" style={INPUT}
                  onFocus={focusOn} onBlur={focusOff}
                />
              </div>
              <div className="col-md-4">
                <label style={LABEL}>Code <span style={{ color: "#ef4444" }}>*</span></label>
                <input
                  type="text" value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="e.g. CBE-01" maxLength={12}
                  style={{ ...INPUT, fontFamily: "monospace" }}
                  onFocus={focusOn} onBlur={focusOff}
                />
                <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginTop: 4 }}>
                  Unique across the estate — a clash is refused.
                </div>
              </div>
            </div>

            <div className="row" style={{ marginBottom: 24 }}>
              <div className="col-md-8">
                <label style={LABEL}>Address</label>
                <input
                  type="text" value={addressLine}
                  onChange={(e) => setAddressLine(e.target.value)}
                  placeholder="e.g. 12, Main Road, Coimbatore" style={INPUT}
                  onFocus={focusOn} onBlur={focusOff}
                />
              </div>
              <div className="col-md-4">
                <NotStored why="core.MstStores has no phone column.">
                  <label style={LABEL}>Phone</label>
                  <input
                    type="text" value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="e.g. 9876543210" style={INPUT}
                  />
                </NotStored>
              </div>
            </div>

            {/* ── Delivery & Pickup ──
                These moved off the store record onto its settings row, so they
                are read through /admin/store-settings/resolved and written back
                with the store. */}
            <div style={SECTION_TITLE}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--mly-heading)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="3" width="15" height="13" />
                <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                <circle cx="5.5" cy="18.5" r="2.5" />
                <circle cx="18.5" cy="18.5" r="2.5" />
              </svg>
              Delivery &amp; Pickup
            </div>

            {settingsLoading && (
              <div style={{ fontSize: 12, color: "var(--mly-text-muted)", marginBottom: 12 }}>
                {editItem ? "Reading this store's settings..." : "Reading the estate-wide defaults..."}
              </div>
            )}

            <div className="row" style={{ marginBottom: 16 }}>
              <div className="col-md-6">
                <div style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "11px 14px", borderRadius: 10, border: "1.5px solid var(--mly-border)",
                }}>
                  <label className="switch" style={{ marginBottom: 0 }}>
                    <input
                      type="checkbox" checked={deliveryEnabled}
                      onChange={(e) => setDeliveryEnabled(e.target.checked)}
                    />
                    <span className="slider round" />
                  </label>
                  <span style={{
                    fontSize: 13, fontWeight: 600,
                    color: deliveryEnabled ? "var(--mly-heading)" : "var(--mly-text-muted)",
                  }}>
                    Delivery {deliveryEnabled ? "Enabled" : "Disabled"}
                  </span>
                </div>
              </div>
              <div className="col-md-6">
                <div style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "11px 14px", borderRadius: 10, border: "1.5px solid var(--mly-border)",
                }}>
                  <label className="switch" style={{ marginBottom: 0 }}>
                    <input
                      type="checkbox" checked={pickupEnabled}
                      onChange={(e) => setPickupEnabled(e.target.checked)}
                    />
                    <span className="slider round" />
                  </label>
                  <span style={{
                    fontSize: 13, fontWeight: 600,
                    color: pickupEnabled ? "var(--mly-heading)" : "var(--mly-text-muted)",
                  }}>
                    Store Pickup {pickupEnabled ? "Enabled" : "Disabled"}
                  </span>
                </div>
              </div>
            </div>

            <div className="row" style={{ marginBottom: 24 }}>
              <div className="col-md-4">
                <label style={LABEL}>Delivery Charge (₹)</label>
                <input
                  type="number" value={deliveryCharge} min="0" step="1"
                  onChange={(e) => setDeliveryCharge(e.target.value)}
                  style={INPUT} onFocus={focusOn} onBlur={focusOff}
                />
              </div>
              <div className="col-md-4">
                <label style={LABEL}>Free Delivery Above (₹)</label>
                <input
                  type="number" value={freeDeliveryAbove} min="0" step="1"
                  onChange={(e) => setFreeDeliveryAbove(e.target.value)}
                  style={INPUT} onFocus={focusOn} onBlur={focusOff}
                />
                <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginTop: 4 }}>
                  0 = never free.
                </div>
              </div>
              <div className="col-md-4">
                <label style={LABEL}>Min Order Value (₹)</label>
                <input
                  type="number" value={minOrderValue} min="0" step="1"
                  onChange={(e) => setMinOrderValue(e.target.value)}
                  style={INPUT} onFocus={focusOn} onBlur={focusOff}
                />
              </div>
            </div>

            {/* ── Hours ── */}
            <div style={SECTION_TITLE}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--mly-heading)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              Hours
            </div>

            <div className="row" style={{ marginBottom: 8 }}>
              <div className="col-md-6">
                <NotStored why="Day boundaries resolve against Asia/Kolkata everywhere.">
                  <label style={LABEL}>Timezone</label>
                  <select
                    value={timezone} onChange={(e) => setTimezone(e.target.value)}
                    style={INPUT}
                  >
                    <option value="Asia/Kolkata">India — IST (Asia/Kolkata)</option>
                    <option value="Asia/Dubai">Gulf — GST (Asia/Dubai)</option>
                    <option value="Asia/Colombo">Sri Lanka (Asia/Colombo)</option>
                    <option value="Asia/Kathmandu">Nepal (Asia/Kathmandu)</option>
                    <option value="Asia/Singapore">Singapore (Asia/Singapore)</option>
                    <option value="Europe/London">UK (Europe/London)</option>
                    <option value="America/New_York">US Eastern (America/New_York)</option>
                    <option value="UTC">UTC</option>
                  </select>
                </NotStored>
              </div>
              <div className="col-md-3">
                <label style={LABEL}>Open Time</label>
                <input
                  type="time" step="900" value={openTime}
                  onChange={(e) => setOpenTime(e.target.value)}
                  style={INPUT} onFocus={focusOn} onBlur={focusOff}
                />
              </div>
              <div className="col-md-3">
                <label style={LABEL}>Close Time</label>
                <input
                  type="time" step="900" value={closeTime}
                  onChange={(e) => setCloseTime(e.target.value)}
                  style={INPUT} onFocus={focusOn} onBlur={focusOff}
                />
              </div>
            </div>
            <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginBottom: 24 }}>
              Closing time must be later than opening time on the same day — an overnight
              window cannot be stored. Both boxes start on the hours this store operates on
              today; clearing one leaves the stored value alone.
            </div>

            {/* ── Milling ── */}
            <div style={SECTION_TITLE}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--mly-heading)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="14.31" y1="8" x2="20.05" y2="17.94" />
                <line x1="9.69" y1="8" x2="21.17" y2="8" />
                <line x1="7.38" y1="12" x2="13.12" y2="2.06" />
                <line x1="9.69" y1="16" x2="3.95" y2="6.06" />
                <line x1="14.31" y1="16" x2="2.83" y2="16" />
                <line x1="16.62" y1="12" x2="10.88" y2="21.94" />
              </svg>
              Milling
            </div>

            <NotStored why="No has_mill / milling_source_store_id columns — milling routing cannot be enforced yet.">
              <div className="row" style={{ marginBottom: 24 }}>
                <div className="col-md-6">
                  <div style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "11px 14px", borderRadius: 10, border: "1.5px solid var(--mly-border)",
                  }}>
                    <label className="switch" style={{ marginBottom: 0 }}>
                      <input
                        type="checkbox" checked={hasMill}
                        onChange={(e) => {
                          setHasMill(e.target.checked);
                          if (e.target.checked) setMillingSourceStore("");
                        }}
                      />
                      <span className="slider round" />
                    </label>
                    <span style={{
                      fontSize: 13, fontWeight: 600,
                      color: hasMill ? "var(--mly-heading)" : "var(--mly-text-muted)",
                    }}>
                      {hasMill ? "Mills in-house" : "No mill on site"}
                    </span>
                  </div>
                </div>

                {!hasMill && (
                  <div className="col-md-6">
                    <label style={LABEL}>Milled By</label>
                    <select
                      value={millingSourceStore}
                      onChange={(e) => setMillingSourceStore(e.target.value)}
                      style={INPUT}
                    >
                      <option value="">— none —</option>
                      {millStores.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}{s.code ? ` (${s.code})` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </NotStored>

            <div style={{ display: "flex", gap: 12 }}>
              <button
                onClick={close}
                style={{
                  padding: "11px 24px", borderRadius: 10,
                  border: "1.5px solid var(--mly-border)", background: "var(--mly-surface-2)",
                  color: "var(--mly-text-soft)", fontWeight: 600, fontSize: 14, cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving || settingsLoading}
                style={{
                  flex: 1, padding: "11px 24px", borderRadius: 10, border: "none",
                  background: (saving || settingsLoading) ? "#a5a4f3" : "var(--mly-heading)",
                  color: "var(--mly-on-accent)", fontWeight: 700, fontSize: 14,
                  cursor: (saving || settingsLoading) ? "not-allowed" : "pointer",
                }}
              >
                {saving ? "Saving..." : editItem ? "Update Store" : "Save Store"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Stores;
